import type { ScenarioDefinition } from '../core/types';
import { eventBus } from '../core/events';
import { CLUTTER_LAYERS } from '../core/constants';
import { pauseClock } from '../core/pause';
import { StepRegistry } from './StepRegistry';
import type { SystemsContext } from './systems-context';

export class ScenarioRunner {
  private systems: SystemsContext;
  private registry: StepRegistry;
  private spawnedVehicleIds: string[] = [];

  private playing = false;
  private onComplete?: () => void;

  followingVehicleId: string | null = null;

  constructor(systems: SystemsContext) {
    this.systems = systems;
    this.registry = new StepRegistry(systems, this);

    eventBus.on('timeline:command', (cmd) => this.handleCommand(cmd));
  }

  async play(scenario: ScenarioDefinition, onComplete?: () => void): Promise<void> {
    this.onComplete = onComplete;
    this.playing = true;
    this.spawnedVehicleIds = [];
    pauseClock.setPaused(false);

    this.hideClutter();
    this.systems.vehicles.hideAllLabels();
    this.systems.city?.enterScenarioMode(scenario.activeInfraIds);
    eventBus.emit('scenario:start', { id: scenario.id, title: scenario.title, hudLabel: scenario.hudLabel, telemetry: scenario.telemetry });
    this.emitState();

    for (let i = 0; i < scenario.steps.length; i++) {
      if (!this.playing) break;
      await this.registry.execute(scenario.steps[i]);
    }

    if (this.playing) {
      this.cleanup();
      this.playing = false;
      pauseClock.setPaused(false);
      eventBus.emit('scenario:end', {});
      this.emitState();
      this.onComplete?.();
    }
  }

  stop(): void {
    this.playing = false;
    pauseClock.setPaused(false);
    pauseClock.flush(); // release any frozen waits so the play() loop exits
    this.cleanup();
    eventBus.emit('scenario:end', {});
    this.emitState();
  }

  pause(): void {
    if (!this.playing || pauseClock.paused) return;
    pauseClock.setPaused(true);
    this.emitState();
  }

  resume(): void {
    if (!this.playing || !pauseClock.paused) return;
    pauseClock.setPaused(false);
    this.emitState();
  }

  togglePause(): void {
    if (!this.playing) return;
    pauseClock.toggle();
    this.emitState();
  }

  /** Scenario is active (not stopped). Pause does NOT make this false —
   *  paused steps freeze inside their pausable waits instead. */
  isPlaying(): boolean {
    return this.playing;
  }

  isPaused(): boolean {
    return pauseClock.paused;
  }

  trackVehicle(vehicleId: string): void {
    this.spawnedVehicleIds.push(vehicleId);
    this.systems.vehicles.setScenarioVehicles(this.spawnedVehicleIds);
  }

  getScenarioVehicleIds(): string[] {
    return this.spawnedVehicleIds;
  }

  private handleCommand(cmd: { action: string }): void {
    switch (cmd.action) {
      case 'pause': this.pause(); break;
      case 'resume': this.resume(); break;
      case 'toggle': this.togglePause(); break;
      case 'stop': this.stop(); break;
    }
  }

  private emitState(): void {
    eventBus.emit('scenario:state', {
      active: this.playing,
      paused: pauseClock.paused,
    });
  }

  private cleanup(): void {
    for (const id of this.spawnedVehicleIds) {
      this.systems.vehicles.remove(id);
      this.systems.routes.removeRoute(id);
    }
    this.spawnedVehicleIds = [];
    this.systems.panels.removeAll();
    this.systems.effects.removeAllEffects();
    this.systems.effects.stopRain();
    this.systems.weather.reset();
    this.systems.camera.cancelAll();
    this.systems.vehicles.showAllLabels();
    this.systems.vehicles.undimAll();
    this.systems.city?.exitScenarioMode();
    this.showClutter();
  }

  private hideClutter(): void {
    const map = this.systems.camera.map as any;
    for (const id of CLUTTER_LAYERS) {
      if (map.getLayer(id)) map.setLayoutProperty(id, 'visibility', 'none');
    }
  }

  private showClutter(): void {
    const map = this.systems.camera.map as any;
    for (const id of CLUTTER_LAYERS) {
      if (map.getLayer(id)) map.setLayoutProperty(id, 'visibility', 'visible');
    }
  }
}
